import React from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDecimal, formatInteger } from '@/lib/language';
import type { Breakdown, BreakdownRow } from '@/lib/scoreBreakdown';
import { needsTouchText } from '@/lib/scoreTouch';
import type { DisapprovalSummary } from '@/lib/disapprovalSummary';
import type { WorkbenchReadinessKey } from '@/lib/ruleWorkbenchTarget';
import type { ReadinessEntry, ReadinessKey } from './feedCheckReadiness';
import { lossInkClass, lossToneClass } from './score-colors';

/**
 * T3 "Zeile 2" (Goal-Prompt "Entlastung Feed-Check", Spec 5.3): loest DREI
 * Listen mit Baustellen (Top-3-Findings im Kopf, Komponenten-Liste im Rumpf,
 * technische Checks als eigene Ueberschrift) durch EINE Karte ab. Sortierung
 * und Gruppierung kommen fertig aus `buildBreakdown`/`readinessEntries`;
 * diese Komponente rechnet nichts, sie ordnet nur an.
 *
 * Zwei Abschnitte:
 * 1. "Was dich Punkte kostet" — die Komponenten aus `buildBreakdown`, in der
 *    gelieferten Reihenfolge (Verlust absteigend). Ersetzt `ScoreBreakdownList`.
 * 2. "Was Google blockiert" — die harten Ablehnungsgruende aus `readiness`
 *    plus die Merchant-Drift-Zeile. Ersetzt `readinessTitle`-Block und
 *    `DriftBlock` als eigene Karte.
 *
 * WP5 A2O-10: die Komponenten-Tabelle bietet KEINEN Knopf je Zeile mehr --
 * alle sechs riefen bis dahin denselben Gesamtlauf auf und versprachen damit
 * eine Teiloptimierung, die es nicht gibt. Statt dessen `cost-action-run`
 * einmal unter der Liste, mit der Menge und einem Satz, was der Lauf tut.
 * Der Knopf ist ein rohes `<button>`, nicht die shadcn-`Button`-Komponente:
 * die Karte hat KEINEN gefuellten Primaerknopf (der lebt auf der
 * Handlungskarte darueber), ein `variant="default"`-Knopf haette hier zwei
 * "wichtigste Aktion"-Signale auf einer Seite erzeugt.
 */

export interface FeedCheckCostListProps {
  breakdown: Breakdown;
  /** Aus `readinessEntries(validationSummary)`. Fehlt/leer + `driftCount` 0 -> Abschnitt 2 entfaellt. */
  readiness?: ReadinessEntry[];
  /** A2-NEU-2: die Pruefungen liessen sich nicht laden; leere `readiness` heisst dann nicht "alles gut". */
  readinessFailed?: boolean;
  onRetryReadiness?: () => void;
  /** `useDriftCount().count`. 0 = keine Drift-Zeile. */
  driftCount?: number;
  /**
   * WP5 A2O-03: von Google fuer Shopping-Anzeigen abgelehnte Produkte
   * (`useFeedDisapprovals`). `null` = nichts zu zeigen, nie "0 abgelehnt".
   */
  googleRejects?: DisapprovalSummary | null;
  /**
   * WP5 A2O-10: EIN Lauf fuer alle Komponenten. Die sechs Zeilenknoepfe davor
   * versprachen "Attribute ergaenzen lassen" und starteten denselben Gesamtlauf
   * (`optimizationType: 'both,attributes'`). Fehlt der Handler oder ist
   * `runCount` 0, gibt es keine Aktion.
   */
  onRunOptimize?: () => void;
  /** Menge des Laufs (`worstTargetCount` der Seite). */
  runCount?: number;
  fixBusy?: boolean;
  /**
   * Warum die Aktionslinks gesperrt sind, im Klartext
   * (`feedCheck.cta.blockedMcRevoked` bzw. `blockedRunning`), oder `undefined`.
   *
   * Ein ausgegrauter Link ohne Grund ist eine Sackgasse mit Extraschritt:
   * `title` braucht Hover, `aria-label` einen Screenreader, und ein
   * `disabled`-Knopf ist gar nicht fokussierbar. Der Vorgaenger
   * `ScoreBreakdownList` trug die Zeile einmal unter der Liste (Befund 6 der
   * Konvergenz-Runde); mit dem Umbau war sie weg, und der Grund stand nur noch
   * in der Handlungskarte rund 200 px weiter oben.
   */
  blockedReason?: string;
  onFillUnitPricing?: () => void;
  fillBusy?: boolean;
  /** Ziel der drei Werkbank-Aktionen; der Schluessel waehlt die Regel (WP5 A2O-02). */
  onOpenWorkbench?: (key: WorkbenchReadinessKey) => void;
  /**
   * WP5 A2O-02: Schluessel, dessen Zaehlung gerade laeuft. Kalt kostet sie auf
   * grossen Feeds mehrere Sekunden; ohne Sperre wirkt der Link tot und laedt
   * zum Mehrfachklick ein.
   */
  workbenchBusyKey?: WorkbenchReadinessKey | null;
  /** Ziel der Drift-Zeile (Automatik in den Einstellungen). */
  onOpenDriftSettings?: () => void;
}

/**
 * Schwere je Ablehnungsgrund: `gtin` (Google lehnt das Produkt ab) und
 * `unitPricing` (in Deutschland nach PAngV abmahnbar) sind ein echtes
 * Risiko und tragen deshalb `destructive-ink`; die uebrigen vier sind
 * Relevanz-/Qualitaetsverlust ohne Abmahnrisiko und bleiben bei `warning-ink`.
 * Fest verdrahtet statt hergeleitet, weil die Zuordnung eine inhaltliche
 * Einstufung ist und keine Formel.
 */
const READINESS_SEVERE: ReadinessKey[] = ['gtin', 'unitPricing'];

/** Erster Satz (bis zum ersten Punkt inkl.) als Halbsatz-Folge neben dem Befund. */
/**
 * Der erste Satz eines Befund-Textes, fuer den Halbsatz neben der Zahl.
 *
 * Getrennt wird an "Punkt PLUS Leerzeichen", nicht am ersten Punkt. Ein Punkt
 * ohne Leerzeichen dahinter beendet keinen Satz, sondern steht in einer Zahl:
 * `readiness.image.consequence` beginnt auf Deutsch mit "Ab dem 31.01.2027
 * lehnt Google Bilder unter 500 x 500 Pixel ab", und die Trennung am ersten
 * Punkt liess davon "Ab dem 31." stehen. Aufgefallen ist das erst beim Lesen
 * der echten Copy -- der Test-Mock von `useTranslation` gibt Schluessel
 * zurueck, und `firstSentence('feedCheck.readiness.image.consequence')`
 * ergibt "feedCheck.", also einen unauffaellig aussehenden Unsinn.
 *
 * Findet sich kein Satzende, wird der ganze Text zurueckgegeben: ein
 * einsatziger Befund ist der Normalfall, kein Sonderfall.
 */
export function firstSentence(text: string): string {
  const idx = text.indexOf('. ');
  return idx === -1 ? text : text.slice(0, idx + 1);
}

/**
 * Info-Symbol als Popover (UX-Audit WP5, A2O-08). Der Radix-Tooltip davor
 * oeffnete nur per Maus-Hover: auf Touch und per Klick passierte nichts, und
 * die Reparaturhinweise waren am Handy unerreichbar. Das Symbol bleibt 24 px,
 * `before:-inset-2.5` erweitert die Trefferflaeche auf 44 px.
 */
function InfoTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground before:absolute before:-inset-2.5 before:content-[''] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring print:hidden"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      {/* A2-NEU-4: 16 px Abstand zum Viewport-Rand (Seitenrand px-4, dieselben
          2rem wie im max-w daneben). Ohne den Wert schob Radix das Popover bei
          390 px buendig an den rechten Rand. */}
      <PopoverContent align="start" collisionPadding={16} className="w-72 max-w-[calc(100vw-2rem)] space-y-1 text-xs">
        {children}
      </PopoverContent>
    </Popover>
  );
}

function ComponentRow({
  row, maxCost, language, showTouch = false,
}: {
  row: BreakdownRow;
  maxCost: number;
  language: string | undefined;
  /**
   * F3: darf diese Zeile den "angefasst oder nicht"-Zusatz tragen?
   *
   * Nur die Zeilen aus `breakdown.weak`, also die drei teuersten. Unter einer
   * Komponente, die den Feed kaum etwas kostet, ist "nicht angefasst" kein
   * Befund, sondern Rauschen -- die Erklaerung gehoert an die Baustelle, an
   * der gehandelt werden soll.
   *
   * Vorgabe `false`: die Zeilen ohne Messung und die nicht anwendbaren rufen
   * dieselbe Komponente und haben keinen Zustand zu zeigen.
   */
  showTouch?: boolean;
}) {
  const { t } = useTranslation('optimization');

  if (!row.measured) {
    return (
      <div data-testid={`cost-row-unmeasured-${row.key}`} className="flex items-center gap-3 py-2">
        <div className="w-24 sm:w-36 shrink-0 min-w-0 truncate text-sm font-medium" title={t(`feedCheck.components.${row.key}`)}>{t(`feedCheck.components.${row.key}`)}</div>
        <div className="flex-1 text-right text-xs text-muted-foreground">
          {t('feedCheck.breakdown.notMeasuredValue')}
        </div>
        <div className="w-10 text-right text-xs text-muted-foreground tabular-nums">
          {t('feedCheck.breakdown.notMeasuredValue')}
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`cost-row-${row.key}`} className="py-2">
      {/* WP5 A2O-17: mobil zweizeilig (Name, Punkte, Score oben; Balken darunter
          in voller Breite), ab sm eine Zeile. Vorher fuenf feste Spalten, die
          Balkenspur war bei 390 px rechnerisch 0 px breit. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:flex-nowrap">
        <div className="order-1 min-w-0 flex-1 flex items-center gap-1.5 sm:order-none sm:flex-none sm:w-36">
          <span className="text-sm font-medium">{t(`feedCheck.components.${row.key}`)}</span>
          <InfoTooltip label={t(`feedCheck.components.${row.key}`)}>
            <p>{t(`feedCheck.issues.${row.key}.consequence`)}</p>
            <p>{t(`feedCheck.issues.${row.key}.fix`)}</p>
          </InfoTooltip>
        </div>
        <div
          data-testid={`cost-track-${row.key}`}
          className="order-4 basis-full h-2.5 rounded-full bg-muted overflow-hidden sm:order-none sm:basis-auto sm:flex-1"
        >
          <div
            data-testid={`cost-bar-${row.key}`}
            className={`h-full rounded-full ${lossToneClass(row.cost)}`}
            style={{ width: `${maxCost > 0 ? Math.max(0, Math.min(100, (row.cost / maxCost) * 100)) : 0}%` }}
          />
        </div>
        <div className={`order-2 whitespace-nowrap text-right text-sm font-bold tabular-nums sm:order-none sm:w-20 ${lossInkClass(row.cost)}`}>
          {t('feedCheck.breakdown.cost', { points: formatDecimal(row.cost, language, 1) })}
        </div>
        <div className="order-3 w-10 text-right text-xs text-muted-foreground tabular-nums sm:order-none">
          {formatInteger(row.score, language)}
        </div>
      </div>
      {showTouch && needsTouchText(row.touch) && (
        <p
          data-testid={`cost-touch-${row.key}`}
          className="mt-0.5 pl-0 text-[11px] leading-relaxed text-muted-foreground"
          title={row.touch === 'unchanged_low' ? t('feedCheck.breakdown.touch.tooltip') : undefined}
        >
          {t(row.touch === 'worse' ? 'feedCheck.breakdown.touch.worse' : 'feedCheck.breakdown.touch.unchangedLow')}
        </p>
      )}
    </div>
  );
}

/**
 * Score v3: eine Komponente, die es fuer die Kategorien dieses Feeds gar nicht
 * gibt (`buildBreakdown().notApplicable`).
 *
 * Sie steht als LETZTE Zeile, ohne Balken, ohne Punktzahl und ohne
 * Aktionslink. Die drei Weglassungen sind je eine Aussage:
 *   Balken     - es gibt keinen Verlust, den man in eine Laenge uebersetzen
 *                koennte; ein Balken der Laenge 0 saehe aus wie "perfekt".
 *   Punktzahl  - `avg_attribute_score` ist bei Anteil 0 `null`, und eine
 *                gerenderte 0 waere die Lesart "null von hundert".
 *   Aktion     - "Attribute ergaenzen" auf einer Kategorie ohne Attribute ist
 *                ein Angebot ohne Wirkung, und es verbraucht Kontingent.
 *
 * Bewusst NICHT dieselbe Zeile wie `measured: false`: dort heisst der Text
 * "nicht gemessen, die naechste Berechnung holt das nach". Hier holt keine
 * Berechnung etwas nach.
 */
function NotApplicableRow({ row }: { row: BreakdownRow }) {
  const { t } = useTranslation('optimization');
  const name = t(`feedCheck.components.${row.key}`);

  return (
    <div data-testid={`cost-row-notapplicable-${row.key}`} className="flex items-center gap-3 py-2">
      <div className="w-24 sm:w-36 shrink-0 min-w-0 truncate text-sm font-medium" title={name}>{name}</div>
      <div className="flex-1 text-right text-xs text-muted-foreground">
        {t('feedCheck.breakdown.notApplicable')}
      </div>
      {/* Leerer Platzhalter statt einer Zahl: die Wertspalte der Zeilen
          darueber traegt den Komponenten-Score, und den gibt es hier nicht.
          "nicht gemessen" stuende daneben wie ein zweiter, anderer Grund. */}
      <div className="w-10 shrink-0" aria-hidden="true" />
    </div>
  );
}

/** Die eine zusammengefasste Zeile fuer alle gemessenen Komponenten ohne Verlust. */
function FineRow({ rows, language }: { rows: BreakdownRow[]; language: string | undefined }) {
  const { t } = useTranslation('optimization');
  const names = rows.map((r) => t(`feedCheck.components.${r.key}`)).join(', ');

  return (
    <div data-testid="cost-row-fine" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2 sm:flex-nowrap">
      {/* Diese Zeile fasst bis zu sechs Namen zusammen und bricht deshalb um,
          statt zu kuerzen: "Highlights, Titel, Goo…" nennt die dritte
          Komponente nicht mehr, und der Sinn der Zeile IST die Aufzaehlung.
          Die Einzelzeilen darueber kuerzen weiter -- dort steht ein Name, und
          ein gekuerzter Einzelname bleibt am `title` ablesbar. */}
      <div className="order-1 min-w-0 flex-1 sm:order-none sm:flex-none sm:w-36">
        <div className="break-words text-sm font-medium">{names}</div>
        {/* A2-NEU-7: der Satz stand als fuenfte Spalte (sm:w-28) rechts und schob
            Spur, Punkte und Score dieser Zeile 124 px nach links. */}
        <div data-testid="cost-row-fine-note" className="text-xs text-muted-foreground">
          {t('feedCheck.costList.fine')}
        </div>
      </div>
      <div className="order-4 basis-full h-2.5 rounded-full bg-muted overflow-hidden sm:order-none sm:basis-auto sm:flex-1" />
      <div className="order-2 whitespace-nowrap text-right text-sm font-bold tabular-nums text-success-ink sm:order-none sm:w-20">
        {/* Eigener Schluessel statt `cost`: der traegt ein Minuszeichen, und
            "-0,0 Pkt." behauptet einen Verlust, den diese Zeile gerade als
            nicht vorhanden ausweist. */}
        {t('feedCheck.breakdown.costZero', { points: formatDecimal(0, language, 1) })}
      </div>
      <div className="order-3 w-10 text-right text-xs text-muted-foreground tabular-nums sm:order-none">
        {formatInteger(100, language)}
      </div>
    </div>
  );
}

function ReadinessRow({
  entry, language, onFillUnitPricing, fillBusy, onOpenWorkbench, workbenchBusy = false,
}: {
  entry: ReadinessEntry;
  language: string | undefined;
  onFillUnitPricing?: () => void;
  fillBusy?: boolean;
  onOpenWorkbench?: (key: WorkbenchReadinessKey) => void;
  workbenchBusy?: boolean;
}) {
  const { t } = useTranslation('optimization');
  const consequence = t(`feedCheck.readiness.${entry.key}.consequence`);
  const fix = t(`feedCheck.readiness.${entry.key}.fix`);
  const severe = READINESS_SEVERE.includes(entry.key);

  const workbenchAction = (entry.key === 'brandPrefix' || entry.key === 'titleLength' || entry.key === 'normClaims') && onOpenWorkbench
    ? (
      <button
        type="button"
        data-testid={`blocked-action-${entry.key}`}
        disabled={workbenchBusy}
        aria-busy={workbenchBusy || undefined}
        onClick={() => onOpenWorkbench(entry.key as WorkbenchReadinessKey)}
        className="min-h-11 text-xs font-bold text-primary hover:underline print:hidden disabled:opacity-50 sm:min-h-0"
      >
        {t(`feedCheck.costList.blocked${entry.key === 'brandPrefix' ? 'BrandPrefix' : entry.key === 'titleLength' ? 'TitleLength' : 'NormClaims'}`)}
      </button>
    )
    : null;
  const fillAction = entry.key === 'unitPricing' && onFillUnitPricing
    ? (
      <button
        type="button"
        data-testid="blocked-action-unitPricing"
        disabled={fillBusy}
        onClick={onFillUnitPricing}
        className="min-h-11 text-xs font-bold text-primary hover:underline print:hidden disabled:opacity-50 sm:min-h-0"
      >
        {t('feedCheck.costList.blockedUnitPricing')}
      </button>
    )
    : null;
  // gtin und image bekommen bewusst KEINE Aktion (Reparatur im Shop). WP5
  // A2O-06: dann wird auch keine Spalte reserviert.
  const action = workbenchAction ?? fillAction;

  return (
    <div data-testid={`blocked-row-${entry.key}`} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-start sm:gap-3">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className={`w-12 shrink-0 text-xl font-bold tabular-nums ${severe ? 'text-destructive-ink' : 'text-warning-ink'}`}>
          {formatInteger(entry.count, language)}
        </div>
        <div className="min-w-0 flex-1 flex items-start gap-1.5">
          <span className="min-w-0 text-sm">
            {t(`feedCheck.readiness.${entry.key}.finding`, { count: entry.count })}
            {' · '}
            <span className="text-muted-foreground">{firstSentence(consequence)}</span>
          </span>
          <InfoTooltip label={t(`feedCheck.readiness.${entry.key}.finding`, { count: entry.count })}>
            <p>{consequence}</p>
            <p>{fix}</p>
          </InfoTooltip>
        </div>
      </div>
      {action && (
        <div data-testid={`blocked-action-slot-${entry.key}`} className="pl-[3.75rem] sm:w-40 sm:shrink-0 sm:pl-0 sm:text-right">
          {action}
        </div>
      )}
    </div>
  );
}

export function FeedCheckCostList({
  breakdown, readiness = [], readinessFailed = false, onRetryReadiness, driftCount = 0, googleRejects = null,
  onRunOptimize, runCount = 0, fixBusy, blockedReason, onFillUnitPricing, fillBusy, onOpenWorkbench, workbenchBusyKey = null, onOpenDriftSettings,
}: FeedCheckCostListProps) {
  const { t, i18n } = useTranslation('optimization');
  const language = i18n?.language;

  const measured = [...breakdown.weak, ...breakdown.strong];
  const lossRows = measured.filter((r) => r.cost >= 0.05);
  const fineRows = measured.filter((r) => r.cost < 0.05);
  const maxCost = Math.max(...lossRows.map((r) => r.cost), 0);

  const totalMissing = Math.round(breakdown.totalCost);
  const totalGap = breakdown.completenessGap === null ? null : Math.round(breakdown.completenessGap);
  const showGapTooltip = totalGap !== null && totalGap >= 1;

  const hasGoogleRejects = !!googleRejects && googleRejects.count > 0;
  const hasHeuristics = readiness.length > 0 || driftCount > 0;
  const showBlocked = hasGoogleRejects || hasHeuristics || readinessFailed;

  return (
    <Card data-testid="cost-list" style={{ border: '1px solid hsl(var(--primary) / 0.08)', boxShadow: 'var(--shadow-sm)' }}>
      <CardContent className="pt-6 pb-6 space-y-5">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-lg font-heading font-bold">{t('feedCheck.costList.title')}</h2>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-xs text-muted-foreground">
              {t('feedCheck.costList.totalMissing', { count: totalMissing })}
            </span>
            {/* Kein Tooltip bei `completenessGap === null`: der Befund ist
                dann in sich widerspruechlich (siehe scoreBreakdown.ts), eine
                erfundene Zahl waere schlimmer als gar keine. */}
            {showGapTooltip && (
              <InfoTooltip label={t('feedCheck.costList.totalGap', { count: totalGap })}>
                <p>{t('feedCheck.costList.totalGap', { count: totalGap })}</p>
              </InfoTooltip>
            )}
          </div>
        </div>

        <div>
          {lossRows.map((row) => (
            <ComponentRow
              key={row.key}
              row={row}
              maxCost={maxCost}
              language={language}
              /* `row.isWeak` und nicht ein festes `true`: diese Schleife
                 rendert `breakdown.weak` UND die Zeilen aus
                 `breakdown.strong`, die noch Punkte kosten (beide liegen in
                 `lossRows`). Nur die drei teuersten tragen den Zusatz. */
              showTouch={row.isWeak}
            />
          ))}
          {fineRows.length > 0 && <FineRow rows={fineRows} language={language} />}
          {breakdown.unmeasured.map((row) => (
            <ComponentRow key={row.key} row={row} maxCost={maxCost} language={language} />
          ))}
          {breakdown.unmeasured.length > 0 && (
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
              {t('feedCheck.breakdown.notMeasuredNote', { count: breakdown.unmeasured.length })}
            </p>
          )}
          {/* Score v3, ganz unten: was diese Kategorien gar nicht fuehren.
              Nach den Messluecken, weil "gibt es hier nicht" kein offener
              Punkt ist, sondern eine abgeschlossene Auskunft. */}
          {breakdown.notApplicable.map((row) => (
            <NotApplicableRow key={row.key} row={row} />
          ))}
          {/* WP5 A2O-10: EINE ehrliche Aktion statt sechs Komponenten-
              Versprechen. Alle sechs Zeilenknoepfe riefen denselben
              Gesamtlauf (`optimizationType: 'both,attributes'`) auf --
              "Attribute ergaenzen lassen" auf der Titel-Zeile war eine
              Behauptung, die die Pipeline nicht einloest. */}
          {onRunOptimize && runCount > 0 && (
            <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between print:hidden">
              <p className="text-xs text-muted-foreground">{t('feedCheck.costList.runHint')}</p>
              <button
                type="button"
                data-testid="cost-action-run"
                disabled={fixBusy}
                title={blockedReason}
                onClick={onRunOptimize}
                className="self-start min-h-11 text-sm font-bold text-primary hover:underline disabled:opacity-50 sm:min-h-0 sm:self-auto"
              >
                {t('feedCheck.costList.runAll', { count: runCount, formatted: formatInteger(runCount, language) })}
              </button>
            </div>
          )}
          {/* S5: der Sperr-Grund einmal unter der Liste, nicht unter jedem der
              Links -- dieselbe Gruppe, dieselbe Aussage. `role="status"`, weil
              ein `disabled`-Knopf nicht fokussierbar ist und `title` einen
              Zeiger braucht. */}
          {onRunOptimize && blockedReason && (
            <p data-testid="cost-blocked-reason" className="print:hidden mt-2 text-xs text-warning-ink" role="status">
              {blockedReason}
            </p>
          )}
        </div>

        {showBlocked && (
          <>
            <Separator />
            {hasGoogleRejects && googleRejects && (
              <div>
                <h3 className="text-sm font-heading font-bold mb-1">{t('feedCheck.costList.googleRejectsTitle')}</h3>
                <div data-testid="blocked-row-disapproved" className="flex items-start gap-3 py-2">
                  <div className="w-12 shrink-0 text-xl font-bold tabular-nums text-destructive-ink">
                    {formatInteger(googleRejects.count, language)}
                  </div>
                  <div className="min-w-0 flex-1 space-y-1 text-sm">
                    <p>
                      {t('feedCheck.costList.googleRejectsFinding', {
                        count: googleRejects.count,
                        formatted: formatInteger(googleRejects.count, language),
                      })}
                    </p>
                    {googleRejects.topIssues.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {t('feedCheck.costList.googleRejectsIssues')}{' '}
                        {googleRejects.topIssues.map((issue, i) => (
                          <span key={issue.code}>
                            {i > 0 && ', '}
                            <span className="font-mono">{issue.code}</span>
                            {` (${formatInteger(issue.count, language)})`}
                          </span>
                        ))}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">{t('feedCheck.costList.googleRejectsFix')}</p>
                  </div>
                </div>
              </div>
            )}
            {readinessFailed && (
              <div data-testid="blocked-row-readiness-failed" role="status" className="flex flex-wrap items-center gap-3 py-2 text-sm text-muted-foreground">
                <span>{t('feedCheck.costList.readinessFailed')}</span>
                {onRetryReadiness && (
                  <Button variant="outline" size="sm" className="print:hidden" onClick={onRetryReadiness}>
                    {t('feedCheck.costList.retry')}
                  </Button>
                )}
              </div>
            )}
            {hasHeuristics && (
              <div>
                <h3 className="text-sm font-heading font-bold mb-1">{t('feedCheck.costList.blockedTitle')}</h3>
                <div>
                  {readiness.map((entry) => (
                    <ReadinessRow
                      key={entry.key}
                      entry={entry}
                      language={language}
                      onFillUnitPricing={onFillUnitPricing}
                      fillBusy={fillBusy}
                      onOpenWorkbench={onOpenWorkbench}
                      workbenchBusy={workbenchBusyKey === entry.key}
                    />
                  ))}
                  {driftCount > 0 && (
                    <div data-testid="blocked-row-drift" className="flex flex-col gap-1 py-2 sm:flex-row sm:items-start sm:gap-3">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="w-12 shrink-0 text-xl font-bold tabular-nums text-warning-ink">
                          {formatInteger(driftCount, language)}
                        </div>
                        <div className="min-w-0 flex-1 flex items-start gap-1.5">
                          <span className="min-w-0 text-sm">
                            {t('feedCheck.costList.driftFinding', { count: driftCount })}
                            {' · '}
                            <span className="text-muted-foreground">{t('feedCheck.costList.driftConsequence')}</span>
                          </span>
                          <InfoTooltip label={t('feedCheck.costList.driftFinding', { count: driftCount })}>
                            <p>{t('feedCheck.costList.driftConsequence')}</p>
                            <p>{t('feedCheck.costList.driftFix')}</p>
                          </InfoTooltip>
                        </div>
                      </div>
                      {onOpenDriftSettings && (
                        <div data-testid="blocked-action-slot-drift" className="pl-[3.75rem] sm:w-40 sm:shrink-0 sm:pl-0 sm:text-right">
                          <button
                            type="button"
                            data-testid="blocked-action-drift"
                            onClick={onOpenDriftSettings}
                            className="min-h-11 text-xs font-bold text-primary hover:underline print:hidden sm:min-h-0"
                          >
                            {t('feedCheck.costList.blockedDrift')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
